import { BookingAvailability } from '@/types/bff/gql-schema';

export enum NeighborhoodHospitalFilter {
  NonF2fHospital = 'non_f2f_hospital',
  F2fHospital = 'f2f_hospital',
}

export enum NeighborhoodHospitalType {
  ALL = 'all',
  SundayOperation = 'sunday_operation',
  NightOperation = 'night_operation',
  Specialist = 'specialist',
}

const NEIGHBORHOOD_HOSPITAL_TYPES_MAP = {
  [NeighborhoodHospitalType.ALL]: '전체',
  [NeighborhoodHospitalType.NightOperation]: '야간 진료',
  [NeighborhoodHospitalType.Specialist]: '전문의',
  [NeighborhoodHospitalType.SundayOperation]: '일요일 진료',
};

export class NeighborhoodHospitalTypes {
  selectedTypeTitle(type?: NeighborhoodHospitalType) {
    return NEIGHBORHOOD_HOSPITAL_TYPES_MAP[`${type ?? NeighborhoodHospitalType.ALL}`];
  }

  get list() {
    return [
      NeighborhoodHospitalType.ALL,
      NeighborhoodHospitalType.SundayOperation,
      NeighborhoodHospitalType.NightOperation,
      NeighborhoodHospitalType.Specialist,
    ];
  }
}

export interface NonFaceToFaceDepartment {
  id: string;
  name: string;
}

export enum EPriceType {
  DIAGNOSIS_ONLY = 'DIAGNOSIS_ONLY',
  WITH_DISPENSATION = 'WITH_DISPENSATION',
}

export interface HospitalVo {
  distance?: number | null;
  hospitalName: string;
  id: string;
  publicId: string;
  name: string;
  reviewAverageRating: number;
  reviewCount: number;
  departments: Array<{
    id: string;
    description?: string | null;
    isProfessional: boolean;
    name: string;
  }>;
  nonBenefitMedicinePricing?: {
    treatmentMedicineName: string;
    originalPrice: number;
    unitDisplayName: string;
    unitTypeDisplayName: string;
    priceType: EPriceType;
  } | null;
  professionalDepartment?: {
    description?: string | null;
    id: string;
    isProfessional: boolean;
    name: string;
  } | null;
  hospitalV2: {
    doctorHospitalId: string;
    doctorId: string;
    hospitalName: string;
    hospitalAddress: {
      roadAddress: string;
      detailAddress?: string | null;
      latitude: number;
      longitude: number;
    };
  };
  bookingInfo: {
    bookingAvailability: BookingAvailability;
    todaySchedule: string;
    tomorrowSchedule: string;
  };
  familyDoctorInfo?: {
    doctorId: string;
  } | null;
}

export class HospitalModel {
  private _distance?: number | null;
  private _hospitalName: string;
  private _id: string;
  private _publicId: string;
  private _name: string;
  private _reviewAverageRating: number;
  private _reviewCount: number;
  private _departments: Array<{
    id: string;
    description?: string | null;
    isProfessional: boolean;
    name: string;
  }>;
  private _nonBenefitMedicinePricing?: {
    treatmentMedicineName: string;
    originalPrice: number;
    unitDisplayName: string;
    unitTypeDisplayName: string;
    priceType: EPriceType;
  } | null;
  private _professionalDepartment?: {
    description?: string | null;
    id: string;
    isProfessional: boolean;
    name: string;
  } | null;
  private _bookingInfo: {
    bookingAvailability: BookingAvailability;
    todaySchedule: string;
    tomorrowSchedule: string;
  };
  private _familyDoctorInfo?: {
    doctorId: string;
  } | null;

  private _latitude: number;
  private _longitude: number;

  constructor(data: HospitalVo) {
    this._distance = data.distance;
    this._hospitalName = data.hospitalName;
    this._id = data.id;
    this._publicId = data.publicId;
    this._name = data.name;
    this._reviewAverageRating = data.reviewAverageRating;
    this._reviewCount = data.reviewCount;
    this._departments = data.departments;
    this._professionalDepartment = data.professionalDepartment;
    this._bookingInfo = data.bookingInfo;
    this._familyDoctorInfo = data.familyDoctorInfo;
    this._nonBenefitMedicinePricing = data.nonBenefitMedicinePricing;

    this._latitude = data.hospitalV2.hospitalAddress.latitude || 0;
    this._longitude = data.hospitalV2.hospitalAddress.longitude || 0;
  }

  isWithDispensation() {
    if (!this._nonBenefitMedicinePricing) {
      return false;
    }

    return this._nonBenefitMedicinePricing.priceType === EPriceType.WITH_DISPENSATION;
  }

  isDiagnosisOnly() {
    if (!this._nonBenefitMedicinePricing) {
      return false;
    }

    return this._nonBenefitMedicinePricing.priceType === EPriceType.DIAGNOSIS_ONLY;
  }

  getReviewCount() {
    if (this._reviewCount >= 100) {
      return '100+';
    }

    return this._reviewCount;
  }

  getPublicId() {
    return this._publicId;
  }

  getHospitalName() {
    return this._hospitalName;
  }

  getName() {
    return this._name;
  }

  getIsBookableToday() {
    return this._bookingInfo.bookingAvailability === BookingAvailability.TodayBookable;
  }

  getNonBenefitMedicinePricing() {
    return this._nonBenefitMedicinePricing?.originalPrice;
  }

  getScheduleByBookingAvailability() {
    if (this.getIsBookableToday()) {
      return this._bookingInfo.todaySchedule;
    }

    return this._bookingInfo.tomorrowSchedule;
  }

  getDistance() {
    return this._distance;
  }

  getDepartments() {
    return this._departments;
  }

  getProfessionalDepartment() {
    return this._professionalDepartment;
  }

  getReviewAverageRating() {
    return this._reviewAverageRating;
  }

  getId() {
    return this._id;
  }

  getFamilyDoctorInfo() {
    return this._familyDoctorInfo;
  }

  getLatitude() {
    return this._latitude;
  }

  getLongitude() {
    return this._longitude;
  }
}
